import S from 's-js';
import { lift } from 's-array';
import { AssertExtends } from 'lib/assertExtends';
import { IAjax } from 'lib/Ajax';
import { notNullish } from 'lib/notNullish';
import UOM from 'lib/UOM';
import { ValidationWatcher } from 'lib/validationWatcher';

import { AddPortCallRequest } from 'data/models/AddPortCallRequest';
import { BerthReservation } from 'data/models/BerthReservation';
import { DataCacheUpdateEntities } from 'data/models/DataCacheUpdate';
import { Measurement, NullableMeasurement } from 'data/models/Measurement';
import { PortCall } from 'data/models/PortCall';
import { VesselSearchResultData } from 'data/models/VesselSearchResult';

import { PCCache } from 'data/PCCache';

import { Entrypoints } from 'core/app/entrypoints';

import { PortCallScheduleHeader, PortCallScheduleHeaderParams } from 'portcall/portcallschedule/header';

import { AddPortCallBerthsTab as BerthsTab, AddPortCallBerthsTabParams } from './berths';
import { AddPortCallCoordinationsTab as CoordinationsTab, AddPortCallCoordinationsTabParams } from './coordinations';
import { AddPortCallItineraryTab as ItineraryTab, AddPortCallItineraryTabParams, InitReservationParams } from './itinerary';
import { AddPortCallPilotsTab as PilotsTab, AddPortCallPilotsTabParams } from './pilots';
import { AddPortCallReviewTab as ReviewTab, AddPortCallReviewTabParams } from './review';
import { AddPortCallSummaryTab as SummaryTab, AddPortCallSummaryTabParams, FinishedOrderData } from './summary';
import { AddPortCallVesselTab as VesselTab, AddPortCallVesselTabParams } from './vessel';

export interface AddPortCallParams extends InitReservationParams { }

export interface AddPortCallTabModel {
    /**
     * Specifies whether the user can proceed forward from this tab. Specifies whether the Next/Submit/OK button
     * should be enabled (true) or disabled (false).
     */
    // NOTE: I originally tried making this CanProceed method optional for our tab model implementations, with
    // the thought that the default if no implementation was provided would be to allow proceeding. But that
    // made it annoying to call CanProceed below and annoying for our tab model implementations to proactively
    // check their compliance w/ this interface via our AssertExtends utility (typescript does stricter checking
    // of constraints like `T extends U` when type U has only optional properties/methods). So instead we'll
    // just say that our tab model implementations have to be explicit about their CanProceed logic.
    CanProceed() : boolean;
}

export enum TabId {
    Berths = 'berths',
    Coordinations = 'coordinations',
    Itinerary = 'itinerary',
    Pilots = 'pilots',
    Review = 'review',
    Summary = 'summary',
    Vessel = 'vessel',
}

export interface BerthsTabInstance        { id : TabId.Berths       ; model : BerthsTab       ; }
export interface CoordinationsTabInstance { id : TabId.Coordinations; model : CoordinationsTab; }
export interface ItineraryTabInstance     { id : TabId.Itinerary    ; model : ItineraryTab    ; }
export interface PilotsTabInstance        { id : TabId.Pilots       ; model : PilotsTab       ; }
export interface ReviewTabInstance        { id : TabId.Review       ; model : ReviewTab       ; }
export interface SummaryTabInstance       { id : TabId.Summary      ; model : SummaryTab      ; }
export interface VesselTabInstance        { id : TabId.Vessel       ; model : VesselTab       ; }

export type TabInstance =
    BerthsTabInstance
  | CoordinationsTabInstance
  | ItineraryTabInstance
  | PilotsTabInstance
  | ReviewTabInstance
  | SummaryTabInstance
  | VesselTabInstance;

export type AddPortCall = ReturnType<typeof AddPortCall>;
export function AddPortCall(
    ajax : IAjax,
    entrypoints : Entrypoints,
    data : PCCache,
    scheduleHeaderModel : (params : PortCallScheduleHeaderParams) => PortCallScheduleHeader,
    berthsTabModel : (params : AddPortCallBerthsTabParams) => BerthsTab,
    coordinationsTabModel : (params : AddPortCallCoordinationsTabParams) => CoordinationsTab,
    itineraryTabModel : (params : AddPortCallItineraryTabParams) => ItineraryTab,
    pilotsTabModel : (params : AddPortCallPilotsTabParams) => PilotsTab,
    reviewTabModel : (params : AddPortCallReviewTabParams) => ReviewTab,
    summaryTabModel : (params : AddPortCallSummaryTabParams) => SummaryTab,
    vesselTabModel : (params : AddPortCallVesselTabParams) => VesselTab,
    params : AddPortCallParams
) {
    const portcall = new PortCall({
        Id: 0,
        LengthOnlyLength: { UOM: UOM.Foot },
        AirDraft: { UOM: UOM.Foot },
        DeepDraft: { UOM: UOM.Foot }
    });
    data.PortCalls.attach(portcall, [
        portcall.Agent,
        portcall.Port,
        portcall.Vessel
    ]);

    const Request = new AddPortCallRequest();
    Request.PortCall(portcall);

    const client = ajax.Url<AddPortCallRequest, DataCacheUpdateEntities>(entrypoints.AddPortCall, {
        useCache: true,
        include: [
            r => r.PortCall,
            _ => portcall.Itinerary,
            _ => portcall.Itinerary().map(pt => pt.BerthReservation),
            _ => portcall.Itinerary().map(pt => pt.BerthReservation()?.CargoActivities),
            _ => portcall.Itinerary().map(pt => pt.BerthReservation()?.CruiseActivities),
            // The server needs us to go via PortCall -> Itinerary -> Schedule -> CoordinationCards and NOT
            // via PortCall -> Schedule -> CoordinationCards, otherwise it can't figure out which itinerary
            // point each date entity is supposed to go with (there are no Ids populated yet).
            _ => portcall.Itinerary().map(pt => pt.Schedule),
            _ => portcall.Itinerary().map(pt => pt.Schedule().map(sd => sd.CoordinationCards)),
            // The server expects us to go via PortCall -> Itinerary -> DeparturePilotJobOrders so
            // it can tell which movement each PJO corresponds to (there are no Ids populated yet).
            _ => portcall.Itinerary().map(pt => pt.DeparturePilotJobOrders),
            // But DON'T include PortCall -> Itinerary -> ArrivalPilotJobOrders. The server doesn't like that
            // since it thinks we might be trying to specify PJOs twice.
            r => r.BatchRequest,
            r => r.BatchRequest()?.Batch,
            r => r.AddVessel,
            r => r.AddVessel()?.OverrideServiceVessel
        ]
    });

    const
        Validation = ValidationWatcher(client),
        ShowValidationErrors = S.value(false),
        FinishedOrder = S.value<FinishedOrderData | null>(null),
        SubmissionSucceeded = () => !!FinishedOrder(),
        Submitting = S.value(false);

    S(() => ShowValidationErrors(Object.keys(Validation()).length > 0));

    const
        currentTabId = S.value(TabId.Vessel),
        ScheduleHeader = scheduleHeaderModel({ PortCall: portcall }),
        berthsTab = berthsTabModel({ PortCall: portcall, ScheduleHeader }),
        coordinationsTab = coordinationsTabModel({ PortCall: portcall, ScheduleHeader }),
        itineraryTab = itineraryTabModel({
            PortCall: portcall,
            Request,
            ScheduleHeader,
            initReservationParams: params
        }),
        pilotsTab = pilotsTabModel({ PortCall: portcall, ScheduleHeader }),
        reviewTab = reviewTabModel({
            PortCall: portcall,
            Request,
            ScheduleHeader,
            OnReviewTab: () => currentTabId() === TabId.Review
        }),
        summaryTab = summaryTabModel({ FinishedOrder }),
        vesselTab = vesselTabModel({ PortCall: portcall, Request }),
        CurrentTab = S<TabInstance>(() => {
            const id = currentTabId();
            return (
                id === TabId.Berths        ? { id, model: berthsTab        } :
                id === TabId.Coordinations ? { id, model: coordinationsTab } :
                id === TabId.Itinerary     ? { id, model: itineraryTab     } :
                id === TabId.Pilots        ? { id, model: pilotsTab        } :
                id === TabId.Review        ? { id, model: reviewTab        } :
                id === TabId.Summary       ? { id, model: summaryTab       } :
                id === TabId.Vessel        ? { id, model: vesselTab        } :
                null as AssertExtends<typeof id, never>
            );
        }),
        AvailableTabs = S(() =>
            SubmissionSucceeded() ?
                [TabId.Summary] :
                [
                    // NOTE: Order matters. List tabs in the order we want users to proceed through them.
                    TabId.Vessel,
                    TabId.Itinerary,
                    TabId.Berths,
                    coordinationsTab.HasActionableContent() ? TabId.Coordinations : null,
                    pilotsTab.IsTabAvailable() ? TabId.Pilots : null,
                    TabId.Review
                ].filter(notNullish)
        ),
        HasPreviousTab = () => AvailableTabs().indexOf(currentTabId()) > 0,
        HasNextTab = () => {
            const idx = AvailableTabs().indexOf(currentTabId());
            return idx >= 0 && idx < AvailableTabs().length - 1;
        },
        CanProceed = () => CurrentTab().model.CanProceed(),
        Loading = () =>
            Submitting()
         || reviewTab.UpdatingPreview();

    S(() => {
        // If current tab is no longer in our available list, jump to the first available tab. In practice
        // this is how we enforce that we should jump to the Summary tab after the user's submission goes
        // through successfully.
        if (AvailableTabs().indexOf(currentTabId()) < 0 && AvailableTabs().length > 0) {
            currentTabId(AvailableTabs()[0]);
        }
    });

    // Computations to keep core data model internally consistent. Rule of thumb: The computations here should
    // be about either 1) sync'ing one part of our Request object graph with another part of that object graph
    // or 2) synthesizing data contributed by multiple Add PC tabs (e.g. the reservation length calculation
    // depends on vessel info from the Vessel tab, berth info from the Itinerary tab, and footmark info from the
    // Berths tab). If your computation is mostly about updating our Request object graph in response to a UI
    // trigger specific to a single tab, it'd probably be clearer to declare it in that tab's viewmodel instead
    // of here.

    SyncPortCallUOMs(portcall);

    // Per-reservation computations
    const externalVessel = () => vesselTab.NewVessel()?.ExternalVessel() ?? null;
    lift(() => portcall.Reservations()).map(br => {
        SetReservationLength(br, portcall, Request, externalVessel);
        ApplyMostUtilizedBerthRule(br);
    });

    return {
        Validation,
        ShowValidationErrors,
        SubmissionSucceeded,
        CurrentTab,
        AvailableTabs,
        HasPreviousTab,
        HasNextTab,
        CanProceed,
        Loading,
        GoToPreviousTab,
        GoToNextTab,
        JumpBackToTab,
        Submit,
        Submitting
    };

    function GoToPreviousTab() {
        S.sample(() => {
            const idx = AvailableTabs().indexOf(currentTabId());
            if (idx > 0) currentTabId(AvailableTabs()[idx - 1]);
        });
    }

    function GoToNextTab() {
        S.sample(() => {
            if (CurrentTab().model.CanProceed()) {
                const idx = AvailableTabs().indexOf(currentTabId());
                if (idx >= 0 && idx < AvailableTabs().length - 1) {
                    currentTabId(AvailableTabs()[idx + 1]);
                }
            }
        });
    }

    function JumpBackToTab(tab : TabId) {
        S.sample(() => {
            const proposedIdx = AvailableTabs().indexOf(tab);
            // For now, only allowing jumping backwards, not forwards, as an easy way of enforcing validation:
            // To go forwards, you have to use GoToNextTab above, and that method ensures that we check the
            // current tab's CanProceed method before navigating. Of course, we COULD allow jumping forwards so
            // long as we also check all intervening tabs' CanProceed methods, but for now the design thinking
            // is that "jump backwards, step forwards" is flexible enough and more teachable.
            if (proposedIdx >= 0 && proposedIdx <= AvailableTabs().indexOf(currentTabId())) {
                currentTabId(tab);
            }
        });
    }

    // DC 08/07/2024 -- Using Move PortCall on the Summary tab causes the PJOs to "null" out (get a DateTime MinValue).
    // It appears to be connected to the cache, and it seems like it's related to logic on the server, i.e. the data is 
    // present in the Request and is lost when it's added into FinishedOrder. The error doesn't appear anywhere else 
    // Move PortCall is used. Temporary solution is to hide Move PortCall from the Add PortCall Summary tab.
    // Note: the ScheduleEvents related to the PJOs (the last 4 entries) have the null/min values.
    async function Submit() {
        if (Submitting()) return;
        Submitting(true);

        try {
            const dataCacheUpdate = await client.Post(Request);
            if (dataCacheUpdate.PortCalls().length === 0) {
                throw new Error('The server response unexpectedly included no new PortCalls even though its status was successful.');
            }
            FinishedOrder({
                PortCalls: dataCacheUpdate.PortCalls(),
                Batch: dataCacheUpdate.RecurringBatches().length > 0 ? dataCacheUpdate.RecurringBatches()[0] : null
            });
        } finally {
            Submitting(false);
        }
    }
}

function SyncPortCallUOMs(PortCall : PortCall) {
    S(() => PortCall.Port()?.PierLengthUOM() && PortCall.LengthOnlyLength.set(
        PortCall
            .LengthOnlyLength
            .convert(PortCall.Port()!.PierLengthUOM()!)
            .roundToNearest(1)
    ));
    S(() => PortCall.Port()?.PierDepthUOM() && (
        PortCall.DeepDraft.set(
            PortCall
                .DeepDraft
                .convert(PortCall.Port()!.PierDepthUOM()!)
                .roundToNearest(0.01)
        ),
        PortCall.AirDraft.set(
            PortCall
                .AirDraft
                .convert(PortCall.Port()!.PierDepthUOM()!)
                .roundToNearest(0.01)
        )
    ));
}

function SetReservationLength(
    Reservation : BerthReservation,
    PortCall : PortCall,
    Request : AddPortCallRequest,
    ExternalVessel : () => VesselSearchResultData | null,
) {
    // Set reservation length. This is purely for client-side purposes (keeping the footmarks widget in sync
    // while filling out the Add PC form). The server applies its own version of the same logic.
    S(() => {
        let vlength : NullableMeasurement | null, vbeam : NullableMeasurement | null;
        if (PortCall.Vessel()) {
            vlength = PortCall.Vessel()!.Length;
            vbeam   = PortCall.Vessel()!.Beam;
        } else if (Request.AddVessel()) {
            const
                ov = Request.AddVessel()!.OverrideServiceVessel(),
                ev = ExternalVessel();
            vlength = ov?.Length.Num() ? ov.Length : ev ? new NullableMeasurement(ev.Length) : null;
            vbeam   = ov?.Beam  .Num() ? ov.Beam   : ev ? new NullableMeasurement(ev.Beam  ) : null;
        } else {
            vlength = vbeam = null;
        }
        const brLength = BerthReservation.calculateLength(
            vlength,
            vbeam,
            PortCall.LengthOnlyLength,
            Reservation.SideTo(),
            PortCall.LengthOnlyLength.UOM() ?? UOM.Foot
        );
        if (brLength?.Num()) {
            // DC: Getting an "Uncaught Error: conflicting values: [metric value] (e.g. 199.9488000007998) is not the same as [imp value] (e.g. 656)".
            // This happens on sites that are set to metric and a user has pre-selected an area on the Berths timeline. It causes the vessel data below
            // the vessel search to not appear, and the footmarks cannot be adjusted correctly on the Berths tab. Upon submission, the PortCall is normal.
            // The issues stems from an order of operations when parameters are passed in, i.e. berth and itinerary info are known at vessel selection. 
            // Add PC (here) running BR.calcLength() (above), and InPort Ops running Br.calcLength() causes an error when Reservation.Length.set() (below) runs.
            // S-JS has an issue because it's being told to set the Num value in InPort Ops to A white it's set to B here.
            // DC 02/08/2022 -- Similar to the PJO Abort issue, S-JS order of operations are preventing the flow of data. Adding a timeout avoids the issue.
            // Will revisit and provide a more appropriate S resolution when applicable.
            setTimeout(() => Reservation.Length.set(brLength as Measurement), 300);
        } else {
            // If we fail to find a usable reservation length (most likely b/c we've chosen an existing vessel
            // for which Beam.Num is null), fallback to a length of 0. That's also what we expect the server to
            // do if we submit in this state.
            Reservation.Length.Num(0);
        }
    });
}

function ApplyMostUtilizedBerthRule(Reservation : BerthReservation) {
    // Refine selected berth as footmarks or selected berth change. This is purely for faster client-side
    // feedback. The server applies its own version of the same logic.
    S(() => {
        const pt = Reservation.Itinerary();
        if (pt === null) return;

        const berth = Reservation.Itinerary()?.Location()?.Berth();
        // Ugly. Skip updating if Berth and LocationId are out-of-sync. This can happen if the user selects a
        // new berth and that changes both LocationId and Footmark; changing Footmark will trigger this
        // computation to re-run in the next tick of S's clock, but it'll take at least one extra clock tick for
        // Berth to reflect the new LocationId value. W/o this guard, we can wind up applying the
        // most-utilized-berth rule to the wrong piergroup.
        if (berth?.Id() !== pt.LocationId()) return;

        const pg = berth?.PierGroup();
        if (pg) {
            const b = pg.MostUtilizedBerth(Reservation.Footmark, Reservation.Length);
            if (b) pt.LocationId(b.Id());
        }
    });
}



// WEBPACK FOOTER //
// ./apps/portcall/addportcall/model.ts